import {
  Button,
  DatePicker,
  Drawer,
  Flex,
  Radio,
  Select,
  Typography
} from "antd"
import type { RangePickerProps } from "antd/lib/date-picker"
import dayjs from "dayjs"
import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"

import { LeadPickerSelect } from "../../../components/Shared/LeadPickerSelect.tsx"
import { ProjectPickerSelect } from "../../../components/Shared/ProjectPickerSelect.tsx"
import { getAttachmentsFilterOptions } from "../../Album/api/attachment.services.ts"
import {
  DATE_RANGE_LABEL_KEYS,
  DATE_RANGE_PRESETS,
  type DateRangePreset
} from "../../Files/helpers/date-range-filter.ts"
import {
  renderDocumentTypeDecoration,
  renderStatusDecoration
} from "../../Files/helpers/render-metadata-decoration.tsx"
import { documentTypeIconLookup } from "../../Files/helpers/document-type-visuals.ts"
import { useDocumentTypeOptions } from "../../Files/hooks/useDocumentTypeOptions.ts"
import { useStatusOptions } from "../../Files/hooks/useStatusOptions.ts"
import {
  EMPTY_GLOBAL_FILES_FILTER_STATE,
  type GlobalFilesAttention,
  type GlobalFilesFilters,
  type GlobalFilesLocation
} from "../types/global-files-filters.types.ts"

const { Text } = Typography
const ANY_LOCATION = "any"

interface GlobalFilesFilterDrawerProps {
  open: boolean
  filters: GlobalFilesFilters
  onClose: () => void
  onChange: (filters: GlobalFilesFilters) => void
}

function FilterSection({
  title,
  children
}: {
  title: string
  children: React.ReactNode
}) {
  return (
    <Flex vertical gap={4}>
      <Text strong className="text-sm">
        {title}
      </Text>
      {children}
    </Flex>
  )
}

export function GlobalFilesFilterDrawer({
  open,
  filters,
  onClose,
  onChange
}: GlobalFilesFilterDrawerProps) {
  const { t } = useTranslation()
  const [draft, setDraft] = useState<GlobalFilesFilters>(filters)
  const [customerOptions, setCustomerOptions] = useState<
    { label: string; value: string }[]
  >([])

  const { options: documentTypes, loadFailed: documentTypesFailed } =
    useDocumentTypeOptions(open)
  const documentTypeIcons = documentTypeIconLookup(documentTypes)
  const { options: statuses, loadFailed: statusesFailed } =
    useStatusOptions(open)

  useEffect(() => {
    if (open) setDraft(filters)
    // Reset only when the drawer opens; `filters` is deliberately not a
    // dependency, matching `FilesFilterDrawer`'s own convention.
  }, [open])

  useEffect(() => {
    // No `project_id` — Global Files needs the company-wide customer list,
    // not one project's own (see `getAttachmentsFilterOptions`'s backend
    // implementation: omitting `project_id` widens it to every project/lead
    // the caller can see, rather than narrowing to none).
    if (!open || customerOptions.length) return
    let cancelled = false
    void getAttachmentsFilterOptions().then((response) => {
      if (cancelled || !response.ok || !response.data) return
      setCustomerOptions(
        (response.data.customers ?? []).map((option) => ({
          label: String(option.label ?? option.value ?? ""),
          value: String(option.value ?? "")
        }))
      )
    })
    return () => {
      cancelled = true
    }
  }, [open, customerOptions.length])

  const patch = (next: Partial<GlobalFilesFilters>) =>
    setDraft((prev) => ({ ...prev, ...next }))

  const handleCancel = () => {
    setDraft(filters)
    onClose()
  }

  const handleApply = () => {
    onChange(draft)
    onClose()
  }

  const selectLocation = (value: GlobalFilesLocation | typeof ANY_LOCATION) =>
    patch({
      location: value === ANY_LOCATION ? null : value,
      // A company file has neither a Project nor a Lead; switching away
      // from Project/Lead drops whichever specific record was picked —
      // simple conditional state, no inference.
      recordId: null,
      recordLabel: null
    })

  const onRangeChange: RangePickerProps["onChange"] = (_dates, dateStrings) => {
    patch({
      dateAfter: dateStrings?.[0] || null,
      dateBefore: dateStrings?.[1] || null
    })
  }

  const hasActiveFilters =
    !!draft.location ||
    !!draft.documentType ||
    !!draft.status ||
    draft.customerIds.length > 0 ||
    !!draft.dateRange ||
    !!draft.attention

  return (
    <Drawer
      title={t("globalFiles.filters.title", "Filters")}
      placement="right"
      width={360}
      open={open}
      onClose={handleCancel}
      extra={
        <Button
          type="link"
          size="small"
          disabled={!hasActiveFilters}
          onClick={() => setDraft(EMPTY_GLOBAL_FILES_FILTER_STATE)}
        >
          {t("files.filters.clearAll")}
        </Button>
      }
      footer={
        <Flex justify="end" gap="small">
          <Button onClick={handleCancel}>{t("files.filters.cancel")}</Button>
          <Button type="primary" onClick={handleApply}>
            {t("files.filters.apply")}
          </Button>
        </Flex>
      }
    >
      <Flex vertical gap={16}>
        <FilterSection title={t("globalFiles.filters.location", "Location")}>
          <Flex vertical gap={6}>
            <Radio.Group
              aria-label={t("globalFiles.filters.location", "Location")}
              value={draft.location ?? ANY_LOCATION}
              onChange={(e) => selectLocation(e.target.value)}
              options={[
                {
                  label: t("globalFiles.filters.locationAny", "Any"),
                  value: ANY_LOCATION
                },
                {
                  label: t("globalFiles.filters.locationCompany", "Company"),
                  value: "company"
                },
                {
                  label: t("globalFiles.filters.locationProject", "Project"),
                  value: "project"
                },
                {
                  label: t("globalFiles.filters.locationLead", "Lead"),
                  value: "lead"
                }
              ]}
            />
            {draft.location === "project" && (
              <ProjectPickerSelect
                aria-label={t("globalFiles.filters.project", "Project")}
                value={draft.recordId ?? undefined}
                initialOptions={
                  draft.recordId && draft.recordLabel
                    ? [{ value: draft.recordId, label: draft.recordLabel }]
                    : []
                }
                onChange={(value, option) => {
                  const picked = Array.isArray(option) ? option[0] : option
                  patch({
                    recordId: (value as string) ?? null,
                    recordLabel: value ? (picked?.label as string) : null
                  })
                }}
              />
            )}
            {draft.location === "lead" && (
              <LeadPickerSelect
                aria-label={t("globalFiles.filters.lead", "Lead")}
                value={draft.recordId ?? undefined}
                initialOptions={
                  draft.recordId && draft.recordLabel
                    ? [{ value: draft.recordId, label: draft.recordLabel }]
                    : []
                }
                onChange={(value, option) => {
                  const picked = Array.isArray(option) ? option[0] : option
                  patch({
                    recordId: (value as string) ?? null,
                    recordLabel: value ? (picked?.label as string) : null
                  })
                }}
              />
            )}
          </Flex>
        </FilterSection>

        <FilterSection title={t("files.filters.documentType")}>
          <Flex align="center" gap={6} className="w-full">
            {draft.documentType &&
              renderDocumentTypeDecoration(
                draft.documentType,
                documentTypeIcons[draft.documentType]
              )}
            <Select<string>
              showSearch
              allowClear
              aria-label={t("files.filters.documentType")}
              placeholder={t("files.filters.documentTypePlaceholder")}
              optionFilterProp="label"
              value={draft.documentType ?? undefined}
              onChange={(value, option) => {
                const picked = Array.isArray(option) ? option[0] : option
                patch({
                  documentType: value ?? null,
                  documentTypeLabel: value ? (picked?.label as string) : null
                })
              }}
              notFoundContent={
                documentTypesFailed
                  ? t("files.documentType.loadFailed")
                  : undefined
              }
              options={documentTypes}
              optionRender={(option) => (
                <Flex align="center" gap={6}>
                  {option.data.value
                    ? renderDocumentTypeDecoration(
                        String(option.data.value),
                        documentTypeIcons[String(option.data.value)]
                      )
                    : null}
                  {option.data.label}
                </Flex>
              )}
              style={{ flex: 1, minWidth: 0 }}
            />
          </Flex>
        </FilterSection>

        <FilterSection title={t("files.filters.status")}>
          <Flex align="center" gap={6} className="w-full">
            {draft.status && renderStatusDecoration(draft.status)}
            <Select<string>
              showSearch
              allowClear
              aria-label={t("files.filters.status")}
              placeholder={t("files.filters.statusPlaceholder")}
              optionFilterProp="label"
              value={draft.status ?? undefined}
              onChange={(value, option) => {
                const picked = Array.isArray(option) ? option[0] : option
                patch({
                  status: value ?? null,
                  statusLabel: value ? (picked?.label as string) : null
                })
              }}
              notFoundContent={
                statusesFailed ? t("files.status.loadFailed") : undefined
              }
              options={statuses}
              optionRender={(option) => (
                <Flex align="center" gap={6}>
                  {option.data.value
                    ? renderStatusDecoration(String(option.data.value))
                    : null}
                  {option.data.label}
                </Flex>
              )}
              style={{ flex: 1, minWidth: 0 }}
            />
          </Flex>
        </FilterSection>

        <FilterSection
          title={t("globalFiles.filters.attention", "Needs Attention")}
        >
          <Select<GlobalFilesAttention>
            allowClear
            aria-label={t("globalFiles.filters.attention", "Needs Attention")}
            placeholder={t("globalFiles.filters.attentionPlaceholder", "Any")}
            value={draft.attention ?? undefined}
            onChange={(value) => patch({ attention: value ?? null })}
            options={[
              {
                value: "needs_attention",
                label: t("globalFiles.filters.attentionAllIssues", "All issues")
              },
              {
                value: "awaiting_signature",
                label: t(
                  "globalFiles.health.awaitingSignature",
                  "Awaiting signature"
                )
              },
              {
                value: "review_overdue",
                label: t("globalFiles.health.reviewOverdue", "Review overdue")
              },
              {
                value: "missing_document_type",
                label: t(
                  "globalFiles.health.missingDocumentType",
                  "Missing document type"
                )
              },
              {
                value: "changes_requested",
                label: t(
                  "globalFiles.health.changesRequested",
                  "Changes requested"
                )
              }
            ]}
            className="w-full"
          />
        </FilterSection>

        <FilterSection title={t("files.filters.customer")}>
          <Select<string[]>
            mode="multiple"
            allowClear
            showSearch
            aria-label={t("files.filters.customer")}
            placeholder={t("files.filters.customerPlaceholder")}
            value={draft.customerIds}
            onChange={(value, options) => {
              const picked = Array.isArray(options)
                ? options
                : options
                  ? [options]
                  : []
              patch({
                customerIds: value,
                customerLabels: picked.map((option) =>
                  String(option.label ?? "")
                )
              })
            }}
            options={customerOptions}
            filterOption={(input, option) =>
              String(option?.label ?? "")
                .toLowerCase()
                .includes(input.toLowerCase())
            }
            className="w-full"
          />
        </FilterSection>

        <FilterSection title={t("files.filters.date")}>
          <Flex gap={6} wrap="wrap">
            <Select<DateRangePreset>
              aria-label={t("files.filters.date")}
              placeholder={t("files.filters.dateRangePresets.anyTime")}
              allowClear
              value={draft.dateRange ?? undefined}
              onChange={(value) =>
                patch({
                  dateRange: value ?? null,
                  ...(value === "custom"
                    ? {}
                    : { dateAfter: null, dateBefore: null })
                })
              }
              options={DATE_RANGE_PRESETS.map((preset) => ({
                label: t(
                  `files.filters.dateRangePresets.${DATE_RANGE_LABEL_KEYS[preset]}`
                ),
                value: preset
              }))}
              className="basis-40 grow"
            />
            {draft.dateRange === "custom" && (
              <DatePicker.RangePicker
                value={
                  draft.dateAfter && draft.dateBefore
                    ? [dayjs(draft.dateAfter), dayjs(draft.dateBefore)]
                    : undefined
                }
                onChange={onRangeChange}
                className="basis-52 grow"
              />
            )}
          </Flex>
        </FilterSection>
      </Flex>
    </Drawer>
  )
}
